import {
  ActionIcon,
  Box,
  createStyles,
  NumberInput,
  Select,
  Skeleton,
  Stack,
  useMantineTheme,
} from '@mantine/core';
import useTranslation from 'next-translate/useTranslation';
import { useEffect, useMemo } from 'react';

import { SignUpHeader } from '~/domains/auth/components/SignUpHeader/SignUpHeader';
import { PAYMENT_TYPE_TO_METHOD, PaymentType, TransactionType } from '~/common/enums/payments.enum';
import { WarningAlert } from '~/domains/common/components/Alerts/Alerts';
import { useIsCanada } from '~/domains/common/hooks/useIsCanada';
import { Text } from '~/domains/design-system/Text';
import { cardTypeToTitle } from '~/domains/payments/constants';
import { PaymentType as WalletPaymentType } from '~/services/wallet/interfaces/wallets.interface';
import dt from '~/testing';
import { IconChevronLeft } from '~/domains/design-system/icons/IconChevronLeft';
import { useIsMobile } from '~/domains/common/hooks/mediaQueries';

import { BalanceBox } from '../components/BalanceBox';
import { usePaymentMethods } from '../hooks/usePaymentMethods';
import CardTypeIcon from '../icons/CardTypeIcon';
import { getIconColor } from '../utils/utils';

import WithdrawalActions from './WithdrawalActions';
import { WithdrawalSummary } from './components/WithdrawalSummary';
import { BankAccount } from './components/BankAccount';
import { useWithdrawalFormContext } from './context/withdrawal.context';
import { useWithdrawalPaymentMethods } from './hooks/useWithdrawalPaymentMethods';

const useStyles = createStyles((theme) => ({
  container: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    flex: 1,
  },
  content: {
    flex: 1,
  },
  actionsWrapper: {
    marginTop: theme.other.spacing._24,
  },
}));

type WithdrawalFormWithdrawalProps = {
  prevStep: () => void;
  isWithdrawing?: boolean;
};

export function WithdrawalFormWithdrawal({
  prevStep,
  isWithdrawing,
}: WithdrawalFormWithdrawalProps) {
  const { t } = useTranslation('payments');
  const { classes } = useStyles();
  const theme = useMantineTheme();
  const isCanada = useIsCanada();

  const form = useWithdrawalFormContext();
  const { values, setFieldValue } = form;
  const { cardOption, paymentType, bankName, bankAccountName, bankAccountId } = values;

  const { data: paymentMethods, isLoading: isLoadingSavedCards } = usePaymentMethods({
    transactionType: TransactionType.WITHDRAWAL,
    paymentType: WalletPaymentType.CARD,
  });
  const { paymentDescription } = useWithdrawalPaymentMethods({ paymentType });

  const isMobile = useIsMobile();

  const cardOptions = useMemo(
    () =>
      paymentMethods
        ? paymentMethods.map((item) => ({
            value: item.id,
            label: t('cardSelectLabel', {
              cardTypeTitle: cardTypeToTitle[item.card.cardType],
              lastDigits: item.card.lastDigits,
              expMonth: item.card.cardExpiry.month,
              expYear: item.card.cardExpiry.year,
            }),
          }))
        : [],
    [paymentMethods, t]
  );

  // Cannot use Mantine's useDidUpdate, because this has to run onMount as well
  useEffect(() => {
    if (
      // No card selected yet
      !cardOption ||
      // Switching payment types (de-select credit/debit card and pick a new default)
      !cardOptions.some(({ value }) => value === cardOption)
    ) {
      setFieldValue('cardOption', cardOptions[0]?.value);
    }
  }, [cardOptions, cardOption, setFieldValue]);

  const currentCardType = useMemo(
    () => paymentMethods?.find((item) => item.id === cardOption)?.card.cardType,
    [cardOption, paymentMethods]
  );

  const isSelectCardVisible = PaymentType.CARD === paymentType;

  return (
    <Box className={classes.container}>
      <ActionIcon
        onClick={prevStep}
        pos={isMobile ? 'relative' : 'absolute'}
        top={isMobile ? undefined : 2}
        left={isMobile ? undefined : -64}
        right={isMobile ? 10 : 0}
        mb={isMobile ? theme.other.spacing._8 : undefined}
        h={32}
        w={32}
      >
        <IconChevronLeft size={32} />
      </ActionIcon>

      {/* Content section that grows to push button to bottom */}
      <Stack spacing={theme.other.spacing._24} className={classes.content}>
        <SignUpHeader
          title={t('withdrawal.form.withdraw.title')}
          copy={t('withdrawal.form.withdraw.copy')}
          isLogoVisible={false}
        />

        <Stack spacing={theme.other.spacing._24}>
          <BalanceBox />
          {bankAccountId && <BankAccount bankAccount={{ bankName, name: bankAccountName }} />}

          {isSelectCardVisible && (
            <Skeleton visible={isLoadingSavedCards}>
              <Select
                data-test-id={dt.payments.withdrawal.withdrawalForm.cardSelect}
                value={cardOption}
                data={cardOptions}
                placeholder="Select Your Card"
                size="lg"
                icon={<CardTypeIcon type={currentCardType} color={getIconColor(theme)} />}
                onChange={(value) => {
                  form.setFieldValue('cardOption', value ?? undefined);
                }}
                withinPortal
                aria-label={t('withdrawal.form.card.label')}
              />
            </Skeleton>
          )}

          {paymentDescription ? (
            <Text variant="body-small" color="gray.6">
              {paymentDescription}
            </Text>
          ) : null}
        </Stack>

        <Stack spacing={theme.other.spacing._24}>
          <NumberInput
            {...form.getInputProps('withdrawalAmount')}
            data-test-id={dt.payments.withdrawal.withdrawalForm.amountInput}
            value={form.getInputProps('withdrawalAmount').value || undefined} // if 0 display placeholder
            precision={2}
            autoFocus
            placeholder={t('withdrawal.form.amount.placeholder')}
            hideControls
            min={0}
            size="lg"
            inputMode="decimal"
            aria-label={t('withdrawal.form.amount.label')}
          />

          {paymentType === PaymentType.CARD && (
            <WarningAlert px="lg" mt="xs">
              {t('withdrawal.warnings.cannotWithdrawToCreditCard')}
            </WarningAlert>
          )}

          <WithdrawalSummary paymentMethod={PAYMENT_TYPE_TO_METHOD[paymentType]} />
        </Stack>
      </Stack>

      {/* Withdraw Button - pinned to bottom */}
      <Box className={classes.actionsWrapper}>
        <Stack spacing={theme.other.spacing._16}>
          <WithdrawalActions isWithdrawing={isWithdrawing} />
          {isCanada && (
            <Stack spacing={0} justify="center">
              <Text variant="body-medium" color="gray.6" align="center">
                {t('withdrawal.canada.canadianDollars')}
              </Text>
              <Text variant="body-medium" color="gray.6" align="center">
                {t('withdrawal.canada.bankFees')}
              </Text>
            </Stack>
          )}
        </Stack>
      </Box>
    </Box>
  );
}
